import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import * as adminService from '../../services/adminService';
import type { AdminAuditLogEntry } from '../../services/adminService';
import './AdminAuditLogPage.css';

interface AuditLogResponse {
  items: AdminAuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const ACTION_OPTIONS = [
  { value: '', labelKey: 'auditLog.filters.allActions', fallback: 'All actions' },
  { value: 'subscription.extend-trial', labelKey: 'auditLog.actions.extendTrial', fallback: 'Extend trial' },
  { value: 'subscription.grant-premium', labelKey: 'auditLog.actions.grantPremium', fallback: 'Grant premium' },
  { value: 'subscription.revoke', labelKey: 'auditLog.actions.revoke', fallback: 'Revoke subscription' },
  { value: 'transaction.refund', labelKey: 'auditLog.actions.refund', fallback: 'Refund transaction' },
  { value: 'user.change-role', labelKey: 'auditLog.actions.changeRole', fallback: 'Change role' },
  { value: 'user.change-account-type', labelKey: 'auditLog.actions.changeAccountType', fallback: 'Change account type' },
  { value: 'user.suspend', labelKey: 'auditLog.actions.suspend', fallback: 'Suspend user' },
  { value: 'user.activate', labelKey: 'auditLog.actions.activate', fallback: 'Activate user' },
  { value: 'user.send-password-reset', labelKey: 'auditLog.actions.sendPasswordReset', fallback: 'Send password reset' },
  { value: 'user.send-verification-email', labelKey: 'auditLog.actions.sendVerificationEmail', fallback: 'Send verification email' },
  { value: 'user.adjust-tokens', labelKey: 'auditLog.actions.adjustTokens', fallback: 'Adjust tokens' },
];

const TARGET_TYPE_OPTIONS = [
  { value: '', labelKey: 'auditLog.filters.allTargets', fallback: 'All targets' },
  { value: 'user', labelKey: 'auditLog.targetTypes.user', fallback: 'User' },
  { value: 'subscription', labelKey: 'auditLog.targetTypes.subscription', fallback: 'Subscription' },
  { value: 'transaction', labelKey: 'auditLog.targetTypes.transaction', fallback: 'Transaction' },
];

const AdminAuditLogPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<AdminAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [limit] = useState(25);

  const [actionFilter, setActionFilter] = useState(() => searchParams.get('action') ?? '');
  const [targetTypeFilter, setTargetTypeFilter] = useState(() => searchParams.get('targetType') ?? '');
  const [adminIdFilter, setAdminIdFilter] = useState(() => searchParams.get('adminId') ?? '');
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('dateFrom') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('dateTo') ?? '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: AuditLogResponse = await adminService.searchAuditLog({
        action: actionFilter || undefined,
        targetType: targetTypeFilter || undefined,
        adminId: adminIdFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        limit,
      });
      setRows(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch {
      setError(t('auditLog.fetchError', 'Failed to load audit log'));
    } finally {
      setLoading(false);
    }
  }, [actionFilter, targetTypeFilter, adminIdFilter, dateFrom, dateTo, page, limit, t]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (actionFilter) next.set('action', actionFilter);
    if (targetTypeFilter) next.set('targetType', targetTypeFilter);
    if (adminIdFilter) next.set('adminId', adminIdFilter);
    if (dateFrom) next.set('dateFrom', dateFrom);
    if (dateTo) next.set('dateTo', dateTo);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [actionFilter, targetTypeFilter, adminIdFilter, dateFrom, dateTo, page, setSearchParams]);

  const renderTargetLink = (row: AdminAuditLogEntry) => {
    if (!row.target_id) return <span className="audit-log-target-none">—</span>;
    if (row.target_type === 'user') {
      return (
        <Link to={`/admin/users/${row.target_id}`} className="audit-log-target-link">
          {row.target_id.substring(0, 8)}…
        </Link>
      );
    }
    if (row.target_type === 'subscription') {
      return (
        <Link to={`/admin/subscriptions/${row.target_id}`} className="audit-log-target-link">
          {row.target_id.substring(0, 8)}…
        </Link>
      );
    }
    return <span>{row.target_id.substring(0, 8)}…</span>;
  };

  return (
    <div className="admin-audit-log-page">
      <div className="audit-log-header">
        <h1>{t('auditLog.title', 'Audit Log')}</h1>
        <p className="audit-log-subtitle">
          {t(
            'auditLog.subtitle',
            'Every admin mutation is recorded here. Use filters to investigate who did what.',
          )}
        </p>
      </div>

      <div className="audit-log-filters">
        <div className="audit-log-filter-group">
          <label>{t('auditLog.filters.action', 'Action')}</label>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey, opt.fallback)}
              </option>
            ))}
          </select>
        </div>

        <div className="audit-log-filter-group">
          <label>{t('auditLog.filters.targetType', 'Target type')}</label>
          <select
            value={targetTypeFilter}
            onChange={(e) => {
              setTargetTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            {TARGET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey, opt.fallback)}
              </option>
            ))}
          </select>
        </div>

        <div className="audit-log-filter-group">
          <label>{t('auditLog.filters.adminId', 'Admin ID')}</label>
          <input
            type="text"
            value={adminIdFilter}
            placeholder="uuid"
            onChange={(e) => setAdminIdFilter(e.target.value)}
            onBlur={() => setPage(1)}
          />
        </div>

        <div className="audit-log-filter-group">
          <label>{t('auditLog.filters.dateFrom', 'From')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="audit-log-filter-group">
          <label>{t('auditLog.filters.dateTo', 'To')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {error && <div className="audit-log-error">{error}</div>}

      <div className="audit-log-meta">
        {t('auditLog.total', { count: total, defaultValue: '{{count}} entries' })}
      </div>

      <div className="audit-log-table-wrapper">
        {loading ? (
          <div className="audit-log-loading">{t('auditLog.loading', 'Loading…')}</div>
        ) : rows.length === 0 ? (
          <div className="audit-log-empty">{t('auditLog.empty', 'No audit entries match the filters.')}</div>
        ) : (
          <table className="audit-log-table">
            <thead>
              <tr>
                <th>{t('auditLog.columns.time', 'Time')}</th>
                <th>{t('auditLog.columns.admin', 'Admin')}</th>
                <th>{t('auditLog.columns.action', 'Action')}</th>
                <th>{t('auditLog.columns.target', 'Target')}</th>
                <th>{t('auditLog.columns.reason', 'Reason')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td className="audit-log-admin-id">{row.admin_id.substring(0, 8)}…</td>
                      <td><code className="audit-log-action">{row.action}</code></td>
                      <td>
                        <span className="audit-log-target-type">{row.target_type}</span>{' '}
                        {renderTargetLink(row)}
                      </td>
                      <td className="audit-log-reason">{row.reason || '—'}</td>
                      <td>
                        {row.metadata && (
                          <button
                            className="audit-log-expand-btn"
                            onClick={() => setExpandedId(isExpanded ? null : row.id)}
                          >
                            {isExpanded
                              ? t('auditLog.hideDetails', 'Hide')
                              : t('auditLog.showDetails', 'Details')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && row.metadata && (
                      <tr className="audit-log-details-row">
                        <td colSpan={6}>
                          <pre className="audit-log-metadata">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="audit-log-pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            {t('auditLog.pagination.prev', 'Previous')}
          </button>
          <span>
            {t('auditLog.pagination.pageOf', {
              page,
              total: totalPages,
              defaultValue: 'Page {{page}} of {{total}}',
            })}
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('auditLog.pagination.next', 'Next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogPage;
